import {
    Chip,
    IconButton,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Tooltip,
    Typography,
} from '@mui/material';
import { httpCodeNotFound } from '../../../utils/constants.utils';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { FileDownload, Payment } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { getBillBase64, getBills } from '../../../services/bills.service';
import { ContractDto } from '../../../services/dtos/contract.dto';
import './bills.table.scss';
import { BillsPaginationDto } from '../../../services/dtos/bills-pagination.dto';
import LinearProgress from '@mui/material/LinearProgress';
import { env } from '../../../env';
import axios, { AxiosError } from 'axios';
import { showAxiosMessageError } from '../../../services/error-message.service';
import { BillDto } from '../../../services/dtos/bill.dto';

/**
 * Composant du tableau des factures
 * @param props Contrat dont les factures sont à afficher
 * @constructor
 */
export default function BillsTable(props: { contract?: ContractDto }) {
    // Snackbar des messages
    const showMessage = useSnackbars();
    // Ordre de tri
    type Order = 'asc' | 'desc';
    // Message générique des erreurs
    const message = 'Erreur lors de la récupération des factures.';
    // Nombre de lignes par page, géré par une variable d'environnement
    const rowsPerPage = Number(env.REACT_APP_NUMBER_ELEMENT_TABLE);
    // Page affichée
    const [page, setPage] = useState(0);
    const [bills, setBills] = useState<BillsPaginationDto>();
    const [loading, setLoading] = useState<boolean>(true);
    const [order, setOrder] = useState<Order>('desc');
    const [orderBy, setOrderBy] = useState<string>('facture.date_exigibilite');
    const [isSearching, setIsSearching] = useState<boolean>(false);

    let headCells = [
        //colonne référence
        {
            id: 'facture.reference',
            label: 'Référence',
            sort: true,
        },
        //colonne Date d'émission
        {
            id: 'facture.date_emission',
            label: "Date d'émission",
            sort: true,
        },
        //colonne Montant
        {
            id: 'facture.montant_restant',
            label: 'Montant',
            sort: true,
        },
        //colonne Date d'exigibilité
        {
            id: 'facture.date_exigibilite',
            label: "Date d'exigibilité",
            sort: true,
        },
        //colonne État
        {
            id: 'etat_paiement',
            label: 'État',
            sort: false,
        },
        //colonne action
        {
            id: 'telecharger',
            label: '',
            sort: false,
        },
    ];

    // Check if props.contract exists and insert the relevant object at the start of the array
    if (!props.contract) {
        headCells.unshift({
            id: 'reference_pdi',
            label: 'Contrat',
            sort: true,
        });
    }

    // Récupération des factures à l'ouverture de la page.
    useEffect(() => {
        const sort = '&order[' + orderBy + ']=' + order;
        getBills(props.contract ? props.contract?.['@id'] : '0', page + 1, rowsPerPage, sort)
            .then(data => {
                setBills(data);
            })
            .catch(error => {
                console.error(message);
                showAxiosMessageError(error, showMessage);
            })
            .finally(() => {
                setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Gestion du téléchargement d'une facture
     * @param contractId Id du contrat
     * @param billRef Référence de la facture
     */
    const handleClickDownload = (contractId: string, billRef: string) => {
        //Récupération du pdf via web service
        getBillBase64(contractId, billRef)
            .then(result => {
                // Conversion du contenu base64 en pdf et ouverture de celui-ci
                fetch(`data:application/pdf;base64,` + result)
                    .then(async pdf => {
                        const file = await pdf.blob();
                        const fileURL = URL.createObjectURL(file);
                        window.open(fileURL);
                    })
                    .catch(error => {
                        console.error('Erreur lors de la conversion de la facture.');
                        showAxiosMessageError(error, showMessage);
                    });
            })
            .catch((error: Error | AxiosError) => {
                if (axios.isAxiosError(error)) {
                    // Gestion de l'erreur 404 sur la récup du fichier
                    if (error?.response?.status === httpCodeNotFound) {
                        showMessage.errorAlert('Aucun fichier disponible au téléchargement.');
                    } else {
                        showAxiosMessageError(error, showMessage);
                    }
                } else {
                    showAxiosMessageError(error, showMessage);
                }
            });
    };

    /**
     * Gestion du changement de page, avec la récupération d'une autre page de factures
     * @param event Événement
     * @param newPage Page à afficher
     */
    const handleOnPageChange = async (event: any, newPage: number) => {
        event.preventDefault();
        setIsSearching(true);
        const sort = '&order[' + orderBy + ']=' + order;
        // Récupération de la page de facture
        getBills(props.contract ? props.contract?.['@id'] : '0', newPage + 1, rowsPerPage, sort)
            .then(data => {
                setBills(data);
                setPage(newPage);
            })
            .catch(error => {
                console.error(message);
                showAxiosMessageError(error, showMessage);
            })
            .finally(() => {
                setIsSearching(false);
            });
    };

    /**
     * Gestion du tri, appelle l'API pour récupérer les factures triées
     * @param property
     */
    const createSortHandler = (property: string) => () => {
        //Gestion des ordres
        setIsSearching(true);
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        const sort = '&order[' + property + ']=' + (isAsc ? 'desc' : 'asc');
        // Récupération des factures via un tri
        getBills(props.contract ? props.contract?.['@id'] : '0', page + 1, rowsPerPage, sort)
            .then(data => {
                setBills(data);
            })
            .catch(error => {
                console.error(message);
                showAxiosMessageError(error, showMessage);
            })
            .finally(() => {
                setIsSearching(false);
            });
    };

    const getEtatPaiement = (row: BillDto) => {
        /*mise en place de chip qui permet de voir si on a payé ou non une facture*/
        if (row.facture.etat_paiement === 'Facture soldée') {
            return <Chip label={'Soldée'} color={'success'} />;
        } else if (
            (row.facture.etat_paiement === 'Facture non soldée' ||
                row.facture.etat_paiement === 'Facture soldée partiellement' ||
                (row.facture.etat_paiement === '' && new Date(row.facture.date_emission) > new Date('2025-04-07'))) &&
            row.facture.urlPaiement !== null
        ) {
            /* Si facture est à payer, on affiche un bouton pour payer la facture*/
            return (
                <>
                    {' '}
                    {row.facture.etat_paiement === 'Facture soldée partiellement' && (
                        <Chip label={'Soldée partiellement'} color={'warning'} />
                    )}
                    <Tooltip title={'Payer ma facture'} arrow>
                        <IconButton href={row.facture.urlPaiement} target={'_blank'}>
                            <Payment />
                        </IconButton>
                    </Tooltip>
                </>
            );
        } else if (row.facture.etat_paiement === '' && new Date(row.facture.date_emission) > new Date('2025-04-07')) {
            return <Chip label={'à payer'} color={'error'} />;
        } else {
            return '';
        }
    };

    return (
        // tableau
        <TableContainer className={'containerBills'} component={Paper} key={props.contract?.['@id']}>
            {/*Affichage des infos du contrat*/}
            {props.contract && props.contract.informations?.abonne && (
                <Typography variant={'h5'}>
                    Contrat : {props.contract.informations.abonne.typeContrat} -{' '}
                    {props.contract.informations.abonne.pdiReference}
                </Typography>
            )}

            {props.contract?.adresseCompteur && (
                <Typography variant={'body1'}>
                    Lieu de consommation :{' '}
                    {props.contract.adresseCompteur?.adresse_complete ||
                        `${props.contract.adresseCompteur?.numero || ''} ${
                            props.contract.adresseCompteur?.rue || ''
                        }, ${props.contract.adresseCompteur?.code_postal || ''} ${
                            props.contract.adresseCompteur?.ville || ''
                        }`.trim()}
                </Typography>
            )}

            {loading ? (
                // Skeleton en attente d'elements
                <Skeleton variant="rectangular" height={250}></Skeleton>
            ) : (
                // Affichage du tableau
                <Table>
                    <TableHead>
                        <TableRow>
                            {/*En-têtes*/}
                            {headCells.map(headCell => (
                                <TableCell
                                    key={headCell.id}
                                    align={'left'}
                                    sx={{ fontWeight: 'bold', fontSize: 'medium' }}
                                >
                                    {/*Gestion du sort sur les colonnes*/}
                                    {headCell.sort ? (
                                        <TableSortLabel
                                            active={orderBy === headCell.id}
                                            direction={orderBy === headCell.id ? order : 'asc'}
                                            onClick={createSortHandler(headCell.id)}
                                        >
                                            {headCell.label}
                                        </TableSortLabel>
                                    ) : (
                                        headCell.label
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    {/* Gestion du chargement des pages/tri avec une progress bar */}
                    <TableBody>
                        {!isSearching ? (
                            // Lignes du tableau
                            bills?.bills?.map(row => (
                                <TableRow key={row.facture.id}>
                                    {!props.contract && (
                                        // N° contrat
                                        <TableCell>{row.reference_pdi}</TableCell>
                                    )}
                                    {/*reference facture*/}
                                    <TableCell>{row.facture.reference}</TableCell>
                                    {/*date d'emission*/}
                                    <TableCell>{new Date(row.facture.date_emission).toLocaleDateString()}</TableCell>
                                    {/*montant*/}
                                    <TableCell>{row.facture.montant_ttc + ' € TTC'}</TableCell>
                                    {/*date d'exigibilité*/}
                                    <TableCell>{new Date(row.facture.date_exigibilite).toLocaleDateString()}</TableCell>
                                    {/*action*/}
                                    <TableCell>{getEtatPaiement(row)}</TableCell>
                                    <TableCell>
                                        {/* Bouton de téléchargement de la facture */}
                                        <Tooltip title={'Télécharger la facture'} arrow>
                                            <IconButton
                                                onClick={() =>
                                                    handleClickDownload(row.facture.contractId, row.facture.reference)
                                                }
                                            >
                                                <FileDownload />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            // lors de la recherche on affiche une progresse bar
                            <TableRow>
                                <TableCell colSpan={headCells.length}>
                                    <LinearProgress />
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            {/*Options de pagination*/}
                            <TablePagination
                                count={bills?.totalItems ? bills?.totalItems : 0}
                                onPageChange={handleOnPageChange}
                                page={page}
                                rowsPerPage={rowsPerPage}
                                rowsPerPageOptions={[rowsPerPage]}
                                labelDisplayedRows={({ from, to, count }) => `${from} à ${to} sur ${count}`}
                                showFirstButton
                                showLastButton
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            )}
        </TableContainer>
    );
}
